/* eslint-disable react/prop-types */
import React, { Children, isValidElement } from "react";

import CircularProgressLoader from "../CircularProgressLoader/CircularProgressLoader";
import Conditional from "../Conditional/Conditional";
import InfiniteScroll from "../InfiniteScroll/InfiniteScroll";
import SearchInput from "../SearchInput/SearchInput";
import Text from "../Typography/Text";
import Caret from "@src/assets/svg/components/Caret";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";

import DataListItem from "./DataListItem";

import styles from "./DataList.module.css";

interface Props {
  children:
    | React.ReactElement<typeof DataListItem>
    | Array<React.ReactElement<typeof DataListItem>>;
  className?: string;
  height?: number | string;
  onEndReached: (entries: IntersectionObserverEntry[]) => void;
  intersectionThreshold?: number;
  showSearch?: boolean;
  searchTerm?: string;
  onSearch?: (s: string) => void;
  isLoading?: boolean;
  onClear?: () => void;
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  backButtonLabel?: string;
  showSearchClearButton?: boolean;
  noResultsText?: string;
}

interface DataListProps extends React.FC<Props> {
  DataListItem: typeof DataListItem;
}

const DataList: DataListProps = ({
  children,
  className,
  height = 500,
  onEndReached,
  searchTerm = "",
  onSearch,
  intersectionThreshold = 1,
  isLoading,
  onClear,
  showBackButton = false,
  onBackButtonClick,
  backButtonLabel = "Back",
  showSearchClearButton = false,
  noResultsText = "No results found",
}) => {
  const renderValidChildren = (children: React.ReactNode) => {
    const validChildren = Children.toArray(children).filter(isValidElement);

    const hasInvalidChild = validChildren.some((child) => {
      const childType = child.type as any;
      const childName = childType.displayName || childType.name;
      return childName !== "DataListItem";
    });

    if (hasInvalidChild) {
      throw new Error(
        "Invalid child element. Only DataListItem components are allowed."
      );
    }

    return validChildren;
  };

  const hasResults = Children.toArray(children).length > 0;

  return (
    <div className={toClassName(styles.container, className)}>
      <Conditional
        condition={showBackButton && onBackButtonClick !== undefined}
      >
        <div className={styles.backButtonContainer}>
          <button
            className={styles.backButton}
            onClick={onBackButtonClick}
            type="button"
          >
            <Caret rotate="left" className={styles.backButtonCaret} />
            <Text variant="bodySmall">{backButtonLabel}</Text>
          </button>
        </div>
      </Conditional>
      <Conditional condition={onSearch !== undefined}>
        <SearchInput
          searchInput={searchTerm}
          setSearchInput={onSearch ?? (() => {})}
          placeholder="Search"
          className={styles.filterSearchTerm}
          showClearButton={showSearchClearButton}
          onClear={onClear}
        />
      </Conditional>
      <InfiniteScroll
        onIntersection={onEndReached}
        intersectionThreshold={intersectionThreshold}
        className={styles.list}
        height={height}
      >
        {/* We cannot use a Conditional component here becuase InfiniteScroll 
        relies on immediate children to calculate visibility intersection */}
        {!hasResults && !isLoading && (
          <DataList.DataListItem iconType={"none"} title={noResultsText} />
        )}
        {renderValidChildren(children)}
        <div className={styles.loaderContainer}>
          <CircularProgressLoader
            outerWidthHeight={25}
            innerWidthHeight={25}
            isLoading={isLoading ?? false}
          />
        </div>
      </InfiniteScroll>
    </div>
  );
};

DataList.DataListItem = DataListItem;

export default DataList;
